<!-- FieldVariableExpensesPanel.vue - Panel para registrar gastos variables de campo -->
<template>
  <div class="field-variable-expenses-panel">
    <!-- Resumen -->
    <v-card class="mb-4" variant="outlined" color="orange">
      <v-card-text>
        <v-row justify="center">
          <v-col cols="6" sm="4">
            <div class="text-center">
              <div class="text-h5 font-weight-bold">{{ variableExpenses.length }}</div>
              <div class="text-caption">Gastos Registrados</div>
            </div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="text-center">
              <div class="text-h5 font-weight-bold text-orange">RD$ {{ formatNumber(totalGastado) }}</div>
              <div class="text-caption">Total del Período</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" color="white" bg-color="orange" slider-color="white" class="mb-4">
      <v-tab value="new">
        <v-icon start>mdi-plus-circle</v-icon>
        Registrar Gasto
      </v-tab>
      <v-tab value="list">
        <v-icon start>mdi-format-list-bulleted</v-icon>
        Historial
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- Tab: Registrar Nuevo Gasto -->
      <v-window-item value="new">
        <v-card variant="outlined">
          <v-card-title>
            <v-icon start color="orange">mdi-cash-plus</v-icon>
            Registrar Gasto Variable
          </v-card-title>

          <v-card-text>
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              <v-icon start size="small">mdi-information</v-icon>
              Los gastos variables son imprevistos de campo que se cubren con el fondo del grupo.
              El documento de evidencia es <strong>obligatorio</strong>.
            </v-alert>

            <v-form ref="formRef" @submit.prevent="submitExpense">
              <v-row>
                <!-- Agencia -->
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="newExpense.agency_id"
                    :items="agencies"
                    item-title="name"
                    item-value="id"
                    label="Agencia *"
                    prepend-icon="mdi-store"
                    :rules="[v => !!v || 'Seleccione una agencia']"
                    density="compact"
                  ></v-autocomplete>
                </v-col>

                <!-- Categoría -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newExpense.categoria"
                    :items="categories"
                    item-title="label"
                    item-value="value"
                    label="Categoría *"
                    prepend-icon="mdi-tag"
                    :rules="[v => !!v || 'Seleccione una categoría']"
                    density="compact"
                  >
                    <template v-slot:item="{ item, props }">
                      <v-list-item v-bind="props">
                        <template v-slot:prepend>
                          <v-icon :color="item.raw.color">{{ item.raw.icon }}</v-icon>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <!-- Concepto -->
                <v-col cols="12">
                  <v-text-field
                    v-model="newExpense.concepto"
                    label="Concepto/Descripción *"
                    prepend-icon="mdi-text"
                    placeholder="Ej: Taxi para visita urgente a agencia X"
                    :rules="[
                      v => !!v || 'Ingrese el concepto',
                      v => v.length >= 5 || 'Mínimo 5 caracteres'
                    ]"
                    counter="200"
                    maxlength="200"
                    density="compact"
                  ></v-text-field>
                </v-col>

                <!-- Monto -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="newExpense.monto"
                    type="number"
                    label="Monto (RD$) *"
                    prepend-icon="mdi-currency-usd"
                    :rules="[
                      v => !!v || 'Ingrese el monto',
                      v => v > 0 || 'El monto debe ser mayor a cero',
                      v => v <= fondoDisponible || `Fondos insuficientes (disponible: RD$ ${formatNumber(fondoDisponible)})`
                    ]"
                    min="1"
                    density="compact"
                  ></v-text-field>
                </v-col>

                <!-- Número de comprobante -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newExpense.comprobante_numero"
                    label="Nº Comprobante/Factura"
                    prepend-icon="mdi-file-document-outline"
                    hint="Opcional"
                    persistent-hint
                    density="compact"
                  ></v-text-field>
                </v-col>

                <!-- Documento de evidencia -->
                <v-col cols="12">
                  <v-file-input
                    v-model="newExpense.documento"
                    label="Documento de Evidencia *"
                    prepend-icon="mdi-paperclip"
                    accept=".pdf,.jpg,.jpeg,.png"
                    :hint="documentoError ? '' : `PDF o imagen del recibo/factura (máx. ${MAX_FILE_SIZE_MB}MB)`"
                    persistent-hint
                    :rules="[v => !!v || 'El documento es obligatorio']"
                    :error="!!documentoError"
                    :error-messages="documentoError"
                    density="compact"
                    @update:model-value="handleDocumentoChange"
                  >
                    <template v-slot:selection="{ fileNames }">
                      <v-chip 
                        size="small" 
                        :color="documentoError ? 'error' : 'success'"
                        class="me-2"
                      >
                        <v-icon start size="small">{{ documentoError ? 'mdi-alert' : 'mdi-check' }}</v-icon>
                        {{ fileNames[0] }}
                        <span v-if="documentoSize" class="ml-1 text-caption">
                          ({{ documentoSize }})
                        </span>
                      </v-chip>
                    </template>
                  </v-file-input>
                </v-col>

                <!-- Observaciones -->
                <v-col cols="12">
                  <v-textarea
                    v-model="newExpense.observaciones"
                    label="Observaciones"
                    prepend-icon="mdi-note-text"
                    rows="2"
                    hint="Información adicional (opcional)"
                    persistent-hint
                    density="compact"
                  ></v-textarea>
                </v-col>
              </v-row>

              <!-- Resumen antes de enviar -->
              <v-alert
                v-if="newExpense.monto > 0 && newExpense.agency_id"
                type="warning"
                variant="tonal"
                class="mt-4"
              >
                <div class="d-flex align-center">
                  <v-icon class="mr-2">mdi-wallet</v-icon>
                  <div>
                    <strong>Se descontarán RD$ {{ formatNumber(newExpense.monto) }}</strong> del fondo del grupo.<br>
                    <span class="text-caption">Fondo después del gasto: RD$ {{ formatNumber(fondoDisponible - (newExpense.monto || 0)) }}</span>
                  </div>
                </div>
              </v-alert>

              <v-card-actions class="mt-4">
                <v-spacer></v-spacer>
                <v-btn variant="text" @click="resetForm">
                  <v-icon start>mdi-eraser</v-icon>
                  Limpiar
                </v-btn>
                <v-btn
                  color="orange"
                  variant="elevated"
                  type="submit"
                  :loading="saving"
                  :disabled="!!documentoError"
                >
                  <v-icon start>mdi-check</v-icon>
                  Registrar Gasto
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- Tab: Historial de Gastos -->
      <v-window-item value="list">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon start>mdi-history</v-icon>
            Historial de Gastos Variables
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-refresh"
              variant="text"
              size="small"
              @click="loadVariableExpenses"
              :loading="loading"
            ></v-btn>
          </v-card-title>

          <v-card-text>
            <!-- Filtros -->
            <v-row class="mb-3" dense>
              <v-col cols="12" md="3">
                <v-autocomplete
                  v-model="filters.agency_id"
                  :items="agencies"
                  item-title="name"
                  item-value="id"
                  label="Agencia"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="loadVariableExpenses"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.categoria"
                  :items="categories"
                  item-title="label"
                  item-value="value"
                  label="Categoría"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="loadVariableExpenses"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="filters.date_range"
                  :items="dateRangeOptions"
                  item-title="label"
                  item-value="value"
                  label="Período"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="applyDateRange"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filters.fecha_desde"
                  type="date"
                  label="Desde"
                  density="compact"
                  hide-details
                  :disabled="filters.date_range && filters.date_range !== 'custom'"
                  @update:model-value="loadVariableExpenses"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filters.fecha_hasta"
                  type="date"
                  label="Hasta"
                  density="compact"
                  hide-details
                  :disabled="filters.date_range && filters.date_range !== 'custom'"
                  @update:model-value="loadVariableExpenses"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Lista de gastos -->
            <v-list v-if="variableExpenses.length" lines="three">
              <v-list-item
                v-for="expense in variableExpenses"
                :key="expense.id"
                class="mb-2 rounded border"
                @click="openDetailDialog(expense)"
              >
                <template v-slot:prepend>
                  <v-avatar :color="getCategoryColor(expense.categoria)" size="40">
                    <v-icon color="white">{{ getCategoryIcon(expense.categoria) }}</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title>
                  <strong>{{ expense.concepto }}</strong>
                  <v-chip size="x-small" class="ml-2" :color="getCategoryColor(expense.categoria)">
                    {{ getCategoryLabel(expense.categoria) }}
                  </v-chip>
                </v-list-item-title>

                <v-list-item-subtitle>
                  <div>
                    <v-icon size="14">mdi-store</v-icon>
                    {{ expense.agency?.name || 'N/A' }} |
                    <v-icon size="14">mdi-currency-usd</v-icon>
                    <strong class="text-orange">RD$ {{ formatNumber(expense.monto) }}</strong>
                  </div>
                  <div class="text-caption">
                    <v-icon size="12">mdi-calendar</v-icon>
                    {{ formatDate(expense.fecha_gasto) }}
                    <span v-if="expense.comprobante_numero" class="ml-2">
                      | <v-icon size="12">mdi-file-document</v-icon> {{ expense.comprobante_numero }}
                    </span>
                  </div>
                </v-list-item-subtitle>

                <template v-slot:append>
                  <div class="d-flex flex-column align-center">
                    <v-icon v-if="expense.tiene_documento" size="small" color="success" title="Tiene documento de evidencia">
                      mdi-paperclip
                    </v-icon>
                  </div>
                </template>
              </v-list-item>
            </v-list>

            <v-alert v-else type="info" variant="tonal">
              No hay gastos variables registrados
              <span v-if="filters.agency_id || filters.categoria">con los filtros seleccionados</span>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <!-- Dialog: Detalle del gasto variable -->
    <v-dialog v-model="detailDialog.show" max-width="650">
      <v-card v-if="detailDialog.expense" class="expense-detail-card">
        <!-- Header con gradiente -->
        <div class="expense-detail-header pa-4" :style="{ background: `linear-gradient(135deg, ${getCategoryColorHex(detailDialog.expense.categoria)} 0%, ${getCategoryColorDark(detailDialog.expense.categoria)} 100%)` }">
          <div class="d-flex align-center">
            <v-avatar size="56" color="white" class="elevation-3">
              <v-icon size="28" :color="getCategoryColor(detailDialog.expense.categoria)">
                {{ getCategoryIcon(detailDialog.expense.categoria) }}
              </v-icon>
            </v-avatar>
            <div class="ml-4 text-white">
              <div class="text-h6 font-weight-bold">{{ detailDialog.expense.concepto }}</div>
              <div class="text-caption opacity-80">
                <v-chip size="x-small" color="white" variant="flat" class="mr-2">
                  {{ getCategoryLabel(detailDialog.expense.categoria) }}
                </v-chip>
                Gasto Variable
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon variant="text" color="white" @click="detailDialog.show = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="pa-0">
          <!-- Monto destacado -->
          <div class="text-center py-5 bg-grey-lighten-4">
            <div class="text-caption text-grey-darken-1 mb-1">MONTO GASTADO</div>
            <div class="text-h4 font-weight-bold text-orange">
              RD$ {{ formatNumber(detailDialog.expense.monto) }}
            </div>
          </div>

          <!-- Información en grid -->
          <v-container class="py-4">
            <v-row dense>
              <!-- Agencia -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-blue-lighten-5">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="blue" class="mr-2">mdi-store</v-icon>
                    <span class="text-caption text-grey-darken-1">Agencia</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ detailDialog.expense.agency?.name || 'N/A' }}
                  </div>
                </div>
              </v-col>

              <!-- Fecha -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-orange-lighten-5">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="orange" class="mr-2">mdi-calendar</v-icon>
                    <span class="text-caption text-grey-darken-1">Fecha del Gasto</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ formatDateLong(detailDialog.expense.fecha_gasto) }}
                  </div>
                </div>
              </v-col>

              <!-- Número de comprobante -->
              <v-col cols="12" sm="6" v-if="detailDialog.expense.comprobante_numero">
                <div class="info-card pa-3 rounded-lg bg-grey-lighten-4">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="grey-darken-1" class="mr-2">mdi-receipt</v-icon>
                    <span class="text-caption text-grey-darken-1">Nº Comprobante</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ detailDialog.expense.comprobante_numero }}
                  </div>
                </div>
              </v-col>

              <!-- Registrado por -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-purple-lighten-5">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="purple" class="mr-2">mdi-account</v-icon>
                    <span class="text-caption text-grey-darken-1">Registrado por</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ detailDialog.expense.supervisor?.name || 'Sistema' }}
                  </div>
                </div>
              </v-col>

              <!-- Fecha de registro -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-teal-lighten-5">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="teal" class="mr-2">mdi-clock-outline</v-icon>
                    <span class="text-caption text-grey-darken-1">Registrado</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ formatDateTime(detailDialog.expense.createdAt) }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Observaciones -->
            <div v-if="detailDialog.expense.observaciones" class="mt-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="18" color="grey-darken-1" class="mr-2">mdi-text</v-icon>
                <span class="text-subtitle-2">Observaciones</span>
              </div>
              <div class="pa-3 rounded-lg bg-grey-lighten-4 text-body-2">
                {{ detailDialog.expense.observaciones }}
              </div>
            </div>

            <!-- Documento de evidencia (soporta BLOB legacy y URL multer) -->
            <div v-if="detailDialog.expense.documento_evidencia || detailDialog.expense.comprobante_url" class="mt-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="18" color="success" class="mr-2">mdi-paperclip</v-icon>
                <span class="text-subtitle-2">Documento de Evidencia</span>
              </div>
              
              <!-- Caso 1: URL de archivo (sistema multer nuevo) -->
              <template v-if="detailDialog.expense.comprobante_url">
                <v-img
                  v-if="!detailDialog.expense.comprobante_url.toLowerCase().endsWith('.pdf')"
                  :src="getFullUrl(detailDialog.expense.comprobante_url)"
                  max-height="350"
                  class="rounded-lg border"
                >
                  <template v-slot:error>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-icon size="48" color="grey">mdi-image-broken</v-icon>
                    </div>
                  </template>
                </v-img>
                <div v-else class="text-center pa-4">
                  <v-icon size="64" color="error">mdi-file-pdf-box</v-icon>
                  <p class="mt-2 mb-2">Documento PDF</p>
                  <v-btn
                    color="primary"
                    :href="getFullUrl(detailDialog.expense.comprobante_url)"
                    target="_blank"
                  >
                    <v-icon start>mdi-open-in-new</v-icon>
                    Abrir PDF
                  </v-btn>
                </div>
              </template>
              
              <!-- Caso 2: BLOB base64 (sistema legacy) -->
              <template v-else-if="detailDialog.expense.documento_evidencia">
                <v-img
                  v-if="detailDialog.expense.documento_evidencia_tipo?.startsWith('image/')"
                  :src="getDocumentDataUri(detailDialog.expense)"
                  max-height="350"
                  class="rounded-lg border"
                ></v-img>
                <v-btn
                  v-else
                  color="primary"
                  variant="tonal"
                  :href="getDocumentDataUri(detailDialog.expense)"
                  :download="detailDialog.expense.documento_evidencia_nombre || 'documento'"
                  block
                >
                  <v-icon start>mdi-download</v-icon>
                  Descargar {{ detailDialog.expense.documento_evidencia_nombre || 'documento' }}
                </v-btn>
              </template>
            </div>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-chip size="small" color="orange" variant="tonal">
            <v-icon start size="14">mdi-cash</v-icon>
            Gasto Variable
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="detailDialog.show = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ApiClient from '@/services/api/api-client'
import { getApiOriginUrl } from '@/config/runtimeConfig'

// Props
const props = defineProps({
  agencies: {
    type: Array,
    default: () => []
  },
  fondoDisponible: {
    type: Number,
    default: 0
  }
})

// Emits
const emit = defineEmits(['expense-created', 'refresh-fondo'])

// Constants
const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

// Categorías
const categories = [
  { value: 'transporte', label: 'Transporte', icon: 'mdi-car', color: 'blue' },
  { value: 'alimentacion', label: 'Alimentación', icon: 'mdi-food', color: 'orange' },
  { value: 'materiales', label: 'Materiales', icon: 'mdi-package-variant', color: 'brown' },
  { value: 'comunicacion', label: 'Comunicación', icon: 'mdi-phone', color: 'green' },
  { value: 'tecnico', label: 'Técnico', icon: 'mdi-wrench', color: 'purple' },
  { value: 'emergencia', label: 'Emergencia', icon: 'mdi-alert', color: 'red' },
  { value: 'otros', label: 'Otros', icon: 'mdi-dots-horizontal', color: 'grey' }
]

// Opciones de rango de fecha
const dateRangeOptions = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'this_week', label: 'Semana actual' },
  { value: 'last_week', label: 'Última semana' },
  { value: 'this_month', label: 'Mes actual' },
  { value: 'last_month', label: 'Último mes' },
  { value: 'custom', label: 'Personalizado' }
]

// State
const activeTab = ref('new')
const loading = ref(false)
const saving = ref(false)
const formRef = ref(null)

const variableExpenses = ref([])
const totalGastado = ref(0)

const filters = ref({
  agency_id: null,
  categoria: null,
  fecha_desde: null,
  fecha_hasta: null,
  date_range: null
})

const newExpense = ref({
  agency_id: null,
  categoria: null,
  concepto: '',
  monto: null,
  comprobante_numero: '',
  observaciones: '',
  documento: null
})

const documentoError = ref(null)
const documentoSize = ref(null)
const documentoBase64 = ref(null)

const detailDialog = ref({
  show: false,
  expense: null
})

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

// Computed
const fondoDisponible = computed(() => props.fondoDisponible)

// Methods
const formatNumber = (num) => {
  return new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  let dateObj
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    dateObj = new Date(date + 'T12:00:00')
  } else {
    dateObj = new Date(date)
  }
  if (isNaN(dateObj.getTime())) return 'Fecha inválida'
  return dateObj.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatDateLong = (date) => {
  if (!date) return 'N/A'
  let dateObj
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    dateObj = new Date(date + 'T12:00:00')
  } else {
    dateObj = new Date(date)
  }
  if (isNaN(dateObj.getTime())) return 'Fecha inválida'
  return dateObj.toLocaleDateString('es-DO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatDateTime = (date) => {
  if (!date) return 'N/A'
  const dateObj = new Date(date)
  if (isNaN(dateObj.getTime())) return 'Fecha inválida'
  return dateObj.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getCategoryColor = (categoria) => {
  const cat = categories.find(c => c.value === categoria)
  return cat?.color || 'grey'
}

// Colores hex para gradientes
const getCategoryColorHex = (categoria) => {
  const colorMap = {
    transporte: '#2196F3',    // blue
    alimentacion: '#FF9800',  // orange
    materiales: '#795548',    // brown
    comunicacion: '#4CAF50',  // green
    tecnico: '#9C27B0',       // purple
    emergencia: '#F44336',    // red
    otros: '#9E9E9E'          // grey
  }
  return colorMap[categoria] || '#9E9E9E'
}

const getCategoryColorDark = (categoria) => {
  const colorMap = {
    transporte: '#1565C0',    // blue-darken-3
    alimentacion: '#E65100',  // orange-darken-4
    materiales: '#4E342E',    // brown-darken-3
    comunicacion: '#2E7D32',  // green-darken-3
    tecnico: '#6A1B9A',       // purple-darken-2
    emergencia: '#C62828',    // red-darken-3
    otros: '#424242'          // grey-darken-3
  }
  return colorMap[categoria] || '#424242'
}

const getCategoryIcon = (categoria) => {
  const cat = categories.find(c => c.value === categoria)
  return cat?.icon || 'mdi-help'
}

const getCategoryLabel = (categoria) => {
  const cat = categories.find(c => c.value === categoria)
  return cat?.label || categoria
}

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
}

// Helper para obtener URL completa de archivos (sistema multer)
const getFullUrl = (path) => {
  if (!path) return ''
  // Si ya es una URL completa, devolverla
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  return `${getApiOriginUrl()}${path}`
}

// Helper para construir Data URI - maneja ambos casos: base64 puro o ya con prefijo
const getDocumentDataUri = (expense) => {
  const doc = expense.documento_evidencia
  if (!doc) return ''
  // Si ya tiene el prefijo data:, retornar tal cual
  if (doc.startsWith('data:')) return doc
  // Si no, construir el Data URI
  const tipo = expense.documento_evidencia_tipo || 'application/octet-stream'
  return `data:${tipo};base64,${doc}`
}

const handleDocumentoChange = async (file) => {
  documentoError.value = null
  documentoSize.value = null
  documentoBase64.value = null
  
  if (!file) return
  
  // Validar tamaño
  if (file.size > MAX_FILE_SIZE_BYTES) {
    documentoError.value = `El archivo es muy grande (${formatFileSize(file.size)}). Máximo permitido: ${MAX_FILE_SIZE_MB}MB`
    documentoSize.value = formatFileSize(file.size)
    return
  }
  
  documentoSize.value = formatFileSize(file.size)
  
  // Convertir a base64
  const reader = new FileReader()
  reader.onload = (e) => {
    documentoBase64.value = e.target.result
  }
  reader.readAsDataURL(file)
}

// Función para aplicar rango de fecha predefinido
const applyDateRange = (range) => {
  const today = new Date()
  const formatDate = (d) => d.toISOString().split('T')[0]
  
  filters.value.date_range = range
  
  switch (range) {
    case 'today':
      filters.value.fecha_desde = formatDate(today)
      filters.value.fecha_hasta = formatDate(today)
      break
    case 'yesterday':
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      filters.value.fecha_desde = formatDate(yesterday)
      filters.value.fecha_hasta = formatDate(yesterday)
      break
    case 'this_week':
      const startOfWeek = new Date(today)
      startOfWeek.setDate(today.getDate() - today.getDay())
      filters.value.fecha_desde = formatDate(startOfWeek)
      filters.value.fecha_hasta = formatDate(today)
      break
    case 'last_week':
      const lastWeekEnd = new Date(today)
      lastWeekEnd.setDate(today.getDate() - today.getDay() - 1)
      const lastWeekStart = new Date(lastWeekEnd)
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6)
      filters.value.fecha_desde = formatDate(lastWeekStart)
      filters.value.fecha_hasta = formatDate(lastWeekEnd)
      break
    case 'this_month':
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      filters.value.fecha_desde = formatDate(startOfMonth)
      filters.value.fecha_hasta = formatDate(today)
      break
    case 'last_month':
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      filters.value.fecha_desde = formatDate(lastMonthStart)
      filters.value.fecha_hasta = formatDate(lastMonthEnd)
      break
    case 'custom':
      // No hacer nada, el usuario define las fechas
      break
    default:
      filters.value.fecha_desde = null
      filters.value.fecha_hasta = null
  }
  
  if (range !== 'custom') {
    loadVariableExpenses()
  }
}

const loadVariableExpenses = async () => {
  loading.value = true
  try {
    const params = {}
    if (filters.value.agency_id) params.agency_id = filters.value.agency_id
    if (filters.value.categoria) params.categoria = filters.value.categoria
    if (filters.value.fecha_desde) params.fecha_desde = filters.value.fecha_desde
    if (filters.value.fecha_hasta) params.fecha_hasta = filters.value.fecha_hasta
    
    const response = await ApiClient.get('/field-supervisor/variable-expenses', { params })
    // ApiClient.get devuelve response.data (axios), que es { success, data: { variable_expenses, ... } }
    const data = response?.data || response
    variableExpenses.value = data?.variable_expenses || []
    totalGastado.value = data?.total_monto || 0
    console.log('[FieldVariableExpenses] Gastos cargados:', variableExpenses.value.length, 'Total:', totalGastado.value)
  } catch (error) {
    console.error('Error cargando gastos variables:', error)
    snackbar.value = {
      show: true,
      message: 'Error al cargar gastos variables',
      color: 'error'
    }
  } finally {
    loading.value = false
  }
}

const submitExpense = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) return
  
  if (!documentoBase64.value) {
    snackbar.value = {
      show: true,
      message: 'El documento de evidencia es obligatorio',
      color: 'error'
    }
    return
  }
  
  saving.value = true
  try {
    const payload = {
      agency_id: newExpense.value.agency_id,
      concepto: newExpense.value.concepto,
      categoria: newExpense.value.categoria,
      monto: newExpense.value.monto,
      comprobante_numero: newExpense.value.comprobante_numero || null,
      observaciones: newExpense.value.observaciones || null,
      documento_evidencia: documentoBase64.value,
      documento_evidencia_nombre: newExpense.value.documento?.name || 'evidencia',
      documento_evidencia_tipo: newExpense.value.documento?.type || 'application/octet-stream'
    }
    
    const response = await ApiClient.post('/field-supervisor/variable-expenses', payload)
    // ApiClient.post devuelve AxiosResponse, necesitamos response.data.data o response.data
    const responseData = response?.data?.data || response?.data || response
    const message = response?.data?.message || responseData?.message || '✅ Gasto registrado correctamente'
    
    snackbar.value = {
      show: true,
      message,
      color: 'success'
    }
    
    resetForm()
    loadVariableExpenses()
    emit('expense-created', responseData?.expense)
    emit('refresh-fondo')
    activeTab.value = 'list'
    
  } catch (error) {
    snackbar.value = {
      show: true,
      message: `❌ ${error.response?.data?.error || error.message || 'Error al registrar gasto'}`,
      color: 'error'
    }
  } finally {
    saving.value = false
  }
}

const resetForm = () => {
  newExpense.value = {
    agency_id: null,
    categoria: null,
    concepto: '',
    monto: null,
    comprobante_numero: '',
    observaciones: '',
    documento: null
  }
  documentoError.value = null
  documentoSize.value = null
  documentoBase64.value = null
  formRef.value?.reset()
}

const openDetailDialog = async (expense) => {
  try {
    // Cargar detalle completo con documento
    const response = await ApiClient.get(`/field-supervisor/variable-expenses/${expense.id}`)
    // ApiClient devuelve response.data, que es { success, data: {...} }
    const expenseData = response?.data || response
    detailDialog.value = {
      show: true,
      expense: expenseData
    }
  } catch (error) {
    console.error('Error cargando detalle:', error)
    // Si falla, mostrar lo que tenemos
    detailDialog.value = {
      show: true,
      expense
    }
  }
}

// Lifecycle
onMounted(() => {
  loadVariableExpenses()
})
</script>

<style scoped>
.field-variable-expenses-panel {
  padding: 8px;
}

/* Estilos para el diálogo de detalle */
.expense-detail-card {
  overflow: hidden;
}

.expense-detail-header {
  color: white;
}

.info-card {
  transition: all 0.2s ease;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style>
